/**
 * EncounterSlice Procedures Reducers
 * 
 * Reducers for managing encounter procedures with priority ordering.
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { ProcedureType } from '../../types/encounterTypes';
import { EncounterSliceState } from '../state';
import { addEditedField } from '../../../shared/sliceUtils';

// =============================================================================
// PROCEDURES CRUD REDUCERS
// =============================================================================

/**
 * Add a new procedure
 */
export const addProcedure: CaseReducer<EncounterSliceState, PayloadAction<ProcedureType>> = (
  state,
  action
) => {
  if (state.currentEncounter) {
    if (!state.currentEncounter.procedures) state.currentEncounter.procedures = [];
    state.currentEncounter.procedures.push(action.payload);
    addEditedField(state, 'procedures');
  }
};

/**
 * Update an existing procedure
 */
export const updateProcedure: CaseReducer<EncounterSliceState, PayloadAction<ProcedureType>> = (
  state,
  action
) => {
  if (state.currentEncounter?.procedures) {
    const idx = state.currentEncounter.procedures.findIndex((p) => p.id === action.payload.id);
    if (idx !== -1) {
      state.currentEncounter.procedures[idx] = action.payload;
      addEditedField(state, 'procedures');
    }
  }
};

/**
 * Remove a procedure and resequence priorities
 */
export const removeProcedure: CaseReducer<EncounterSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  if (state.currentEncounter?.procedures) {
    // Find the procedure to remove and get its priority
    const procedureToRemove = state.currentEncounter.procedures.find((p) => p.id === action.payload);
    const removedPriority = procedureToRemove?.priority || 0;

    // Remove the procedure
    state.currentEncounter.procedures = state.currentEncounter.procedures.filter(
      (p) => p.id !== action.payload
    );

    // Resequence priorities for procedures that had higher priorities
    state.currentEncounter.procedures = state.currentEncounter.procedures.map((procedure) => {
      if (procedure.priority && procedure.priority > removedPriority) {
        return {
          ...procedure,
          priority: procedure.priority - 1,
          isPrimary: procedure.priority - 1 === 1, // Update isPrimary based on new priority
        };
      }
      return procedure;
    });

    addEditedField(state, 'procedures');
  }
};

/**
 * Set the entire procedures array (replace all)
 */
export const setProcedures: CaseReducer<EncounterSliceState, PayloadAction<ProcedureType[]>> = (
  state,
  action
) => {
  if (state.currentEncounter) {
    state.currentEncounter.procedures = action.payload;
    addEditedField(state, 'procedures');
  }
};

// =============================================================================
// PROCEDURES PRIORITY REDUCERS
// =============================================================================

/**
 * Move a procedure up in priority
 */
export const moveProcedureUp: CaseReducer<EncounterSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  if (state.currentEncounter?.procedures) {
    const procedures = state.currentEncounter.procedures;
    // Sort by priority first to ensure correct order
    const sortedProcedures = [...procedures].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const procedureIndex = sortedProcedures.findIndex((p) => p.id === action.payload);

    if (procedureIndex > 0) {
      const currentProcedure = sortedProcedures[procedureIndex];
      const previousProcedure = sortedProcedures[procedureIndex - 1];

      // Swap priorities
      const tempPriority = currentProcedure.priority;
      currentProcedure.priority = previousProcedure.priority;
      previousProcedure.priority = tempPriority;

      // Update isPrimary based on new priorities
      currentProcedure.isPrimary = currentProcedure.priority === 1;
      previousProcedure.isPrimary = previousProcedure.priority === 1;

      // Update the original array with the modified procedures
      const currentOriginalIndex = procedures.findIndex((p) => p.id === currentProcedure.id);
      const previousOriginalIndex = procedures.findIndex((p) => p.id === previousProcedure.id);

      if (currentOriginalIndex !== -1) {
        procedures[currentOriginalIndex] = currentProcedure;
      }
      if (previousOriginalIndex !== -1) {
        procedures[previousOriginalIndex] = previousProcedure;
      }

      addEditedField(state, 'procedures');
    }
  }
};

/**
 * Move a procedure down in priority
 */
export const moveProcedureDown: CaseReducer<EncounterSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  if (state.currentEncounter?.procedures) {
    const procedures = state.currentEncounter.procedures;
    // Sort by priority first to ensure correct order
    const sortedProcedures = [...procedures].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const procedureIndex = sortedProcedures.findIndex((p) => p.id === action.payload);

    if (procedureIndex < sortedProcedures.length - 1 && procedureIndex >= 0) {
      const currentProcedure = sortedProcedures[procedureIndex];
      const nextProcedure = sortedProcedures[procedureIndex + 1];

      // Swap priorities
      const tempPriority = currentProcedure.priority;
      currentProcedure.priority = nextProcedure.priority;
      nextProcedure.priority = tempPriority;

      // Update isPrimary based on new priorities
      currentProcedure.isPrimary = currentProcedure.priority === 1;
      nextProcedure.isPrimary = nextProcedure.priority === 1;

      // Update the original array with the modified procedures
      const currentOriginalIndex = procedures.findIndex((p) => p.id === currentProcedure.id);
      const nextOriginalIndex = procedures.findIndex((p) => p.id === nextProcedure.id);

      if (currentOriginalIndex !== -1) {
        procedures[currentOriginalIndex] = currentProcedure;
      }
      if (nextOriginalIndex !== -1) {
        procedures[nextOriginalIndex] = nextProcedure;
      }

      addEditedField(state, 'procedures');
    }
  }
};
